/**
 * PWA Service Worker Registration and Management
 */

export interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

/**
 * Register service worker for PWA functionality
 */
export function registerServiceWorker(config?: ServiceWorkerConfig): void {
  // Only register in production
  if (import.meta.env.DEV) {
    console.log("[PWA] Service worker registration skipped in development");
    return;
  }

  // Check if service workers are supported
  if (!("serviceWorker" in navigator)) {
    console.warn("[PWA] Service workers are not supported in this browser");
    return;
  }

  window.addEventListener("load", () => {
    const swUrl = "/sw.js";

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        console.log("[PWA] Service worker registered:", registration);

        // Check for updates
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;

          installingWorker.onstatechange = () => {
            if (installingWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // New update available
                console.log("[PWA] New content available, please refresh");
                config?.onUpdate?.(registration);
              } else {
                // Content cached for offline use
                console.log("[PWA] Content cached for offline use");
                config?.onSuccess?.(registration);
              }
            }
          };
        };
      })
      .catch((error) => {
        console.error("[PWA] Service worker registration failed:", error);
        config?.onError?.(error);
      });
  });
}

/**
 * Unregister service worker
 */
export function unregisterServiceWorker(): Promise<boolean> {
  if ("serviceWorker" in navigator) {
    return navigator.serviceWorker.ready
      .then((registration) => {
        return registration.unregister();
      })
      .catch((error) => {
        console.error("[PWA] Service worker unregistration failed:", error);
        return false;
      });
  }
  return Promise.resolve(false);
}

/**
 * Check if app is running as PWA
 */
export function isPWA(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    document.referrer.includes("android-app://")
  );
}

/**
 * Prompt user to install PWA
 */
export function setupInstallPrompt(): void {
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar from appearing
    e.preventDefault();
    const promptEvent = e as BeforeInstallPromptEvent;
    deferredPrompt = promptEvent;

    // Optionally, show your own install button
    console.log("[PWA] Install prompt available");

    // Dispatch custom event for UI to handle
    window.dispatchEvent(
      new CustomEvent("pwa-install-available", {
        detail: { prompt: deferredPrompt },
      })
    );
  });

  window.addEventListener("appinstalled", () => {
    console.log("[PWA] App installed successfully");
    deferredPrompt = null;
  });
}

/**
 * Show install prompt
 */
export async function showInstallPrompt(prompt: BeforeInstallPromptEvent | null): Promise<boolean> {
  if (!prompt) {
    console.warn("[PWA] Install prompt not available");
    return false;
  }

  prompt.prompt();
  const { outcome } = await prompt.userChoice;

  console.log(
    `[PWA] User ${outcome === "accepted" ? "accepted" : "dismissed"
    } install prompt`
  );
  return outcome === "accepted";
}
